import { useCallback, useEffect, useRef } from 'react';
import { createSearchParams, useSearchParams } from 'react-router-dom';
import useRouter from '../navigation/useRouter';
import { useTranslation } from 'react-i18next';
import { GiftCardsCheckoutResponse, useGiftCardsCheckoutMutation } from '../../store/api-slices';
import { useSelector } from 'react-redux';
import { selectPaymentProps } from '../../store/slices/OrderSlice';
import { selectCompanyId } from '../../store/slices/InitialDataSlice';
import { GenericOrder, IMap, OnlineShopWebViewPaymentResponseInterface } from '../../services/exports/Interfaces';
import usePaymentFlow from '../../hooks/payment/usePaymentFlow';
import {
  ERROR_CODES,
  OnlineShopWebViewMessageTypes,
  OrderType,
  PaymentGateway,
  PaymentResultErrorCode,
  PaymentStatus,
  SCREENS,
} from '../../services/exports/Constants';
import useAlert from '../../hooks/utility/useAlert';
import { StoreInterface } from '../../store/types';
import { ReservedHttpResponseCodes, ValidationErrorResponseData } from '../../services/exports/Types';
import { RedirectStatus } from '../../pages/OrderDetailsScreen';
import { setUtm } from '../../services/exports/Cookies';
import collect from 'collect.js';
import { SectionType } from '../../components-library/checkout/sections/CheckoutInfoBag';
import toast from 'react-hot-toast';
import { AxiosResponse } from 'axios';
import { OTP_VERIFICATION_PHONE_NUMBER_HEADER } from '../../modals/OtpVerificationModal';
import useMobileApp from '../mobile-app/useMobileApp';
import { Routes } from '../../modules/mobile/exports/Enums';
// `import type`: these are types only, and a value import here pulls useOrderCheckout ->
// OrderFlowProvider -> OrderManager -> store/index into the runtime graph for nothing.
import type { CheckoutFunction, CheckoutOptions } from './useOrderCheckout';
import useNavigate from '../navigation/useNavigate';
import WindowResource from '../../services/resources/WindowResource';
import str from '../../services/helpers/Stringable';
import { PaymentReturnUrlPlaceholder } from './useCheckout';
import { withoutOffsiteReturnParams } from './useSpreedlyOffsitePayment';
import type { ConfirmPaymentFunction, GenerateOrderReturnUrlFunction, PaymentErrorOptions } from './useCheckout';
import { useGiftCardsBasket } from '../basket/useGiftCardBasket';
import paymentLog, { PaymentGatewayKey } from '../../services/helpers/PaymentLogger';
import useOtpStepUp from './useOtpStepUp';
import useInitialData from '../global/useInitialData';
import { STRIPE_ELEMENT_RULES, stripeElementValidationMessage } from '../../utils/stripe';

interface Props {
  loading: boolean;
  setLoading: (loading: boolean) => void;
}

export function useGenerateGiftCardReturnUrl() {
  const { isMobileApp } = useMobileApp();
  const { generatePath } = useNavigate();

  return {
    generateReturnUrl: useCallback<GenerateOrderReturnUrlFunction>(
      (replace = {}) => {
        const base = isMobileApp
          ? `${WindowResource.getOriginWithPathname()}?navigate=${Routes.GiftCardOrderDetails}&uuid=${PaymentReturnUrlPlaceholder.OrderUUID}&payment_gateway=${PaymentReturnUrlPlaceholder.PaymentGateway}&order_type=${OrderType.GiftCard}&mobile_app=true`
          : `${WindowResource.getOrigin()}${generatePath(`/${SCREENS.GIFT_CARDS_ORDER_DETAILS}/${PaymentReturnUrlPlaceholder.OrderUUID}?payment_gateway=${PaymentReturnUrlPlaceholder.PaymentGateway}&order_type=${OrderType.GiftCard}`)}`;

        return str(base).replaceAll(replace).toString();
      },
      [isMobileApp, generatePath],
    ),
  };
}

export function useHandleGiftCardCheckoutResult(props?: any) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Payment:Buttons:PayButton' });

  const [searchParams] = useSearchParams();
  const { navigate, reset } = useRouter();
  const { onlinePaymentsGateway, selectedPaymentMethod } = usePaymentFlow();
  const alert = useAlert();
  const { isMobileApp } = useMobileApp();

  const onPaymentError = (options: PaymentErrorOptions = {}): boolean => {
    // See useOrderCheckout: a cancelled 3DS challenge is the customer's decision — the alert
    // still shows (the order is abandoned), but it is a breadcrumb, not a Sentry issue.
    if (!options.cancelled) {
      paymentLog.error(onlinePaymentsGateway ?? 'unknown', 'gift_card_payment_unsuccessful', {
        payment_method: selectedPaymentMethod,
      });
    }

    if (options.silent) {
      return false;
    }

    alert.error({
      title: t('error_messages.payment_unsuccessful.title'),
      description: t('error_messages.payment_unsuccessful.description'),
    });

    if (onlinePaymentsGateway === PaymentGateway.Adyen || options.backToCheckout) {
      navigate({
        web: {
          to: SCREENS.GIFT_CARDS_CHECKOUT,
        },
        mobile: {
          path: Routes.GiftCardCheckout,
        },
      });
    }

    return false;
  };

  const onPaymentSuccess = (order: GenericOrder): boolean => {
    setUtm(null);

    const updatedSearchParams = {
      redirect_status: RedirectStatus.succeeded,
      ...withoutOffsiteReturnParams(Object.fromEntries(searchParams)),
    };

    if (isMobileApp) {
      reset();
    }

    navigate({
      web: {
        to: {
          pathname: `${SCREENS.GIFT_CARDS_ORDER_DETAILS}/${order.uuid}`,
          search: createSearchParams(updatedSearchParams).toString(),
        },
      },
      mobile: {
        path: Routes.GiftCardOrderDetails,
        payload: {
          ...createSearchParams(updatedSearchParams),
          uuid: order.uuid,
        },
      },
    });

    return true;
  };

  return {
    onPaymentError,
    onPaymentSuccess,
  };
}

export function useGiftCardsCheckoutErrors() {
  const [, { error }] = useGiftCardsCheckoutMutation();

  return {
    validationErrors:
      (error as any)?.status === ReservedHttpResponseCodes.ValidationError
        ? (error as any)?.data?.response?.data
        : undefined,
  };
}

const useGiftCardCheckout = ({ loading, setLoading }: Props) => {
  const { t, i18n } = useTranslation(undefined, { keyPrefix: 'Components:Payment:Buttons:PayButton' });
  const alert = useAlert();
  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const [checkoutMtn, { isLoading, data }] = useGiftCardsCheckoutMutation();
  const _loading = isLoading || loading;
  const paymentProps = useSelector(selectPaymentProps);
  const companyId = useSelector(selectCompanyId);
  const { item: giftCardBasket } = useGiftCardsBasket();
  const { selectedPaymentMethod, toggleCheckoutSection, onlinePaymentsGateway, shouldSavePaymentInfo } =
    usePaymentFlow();
  const { isMobileApp } = useMobileApp();
  const { onPaymentError, onPaymentSuccess } = useHandleGiftCardCheckoutResult();
  const { refresh } = useInitialData();
  // Every gateway value is a PaymentGatewayKey — the old Adyen/Stripe ladder labelled Spreedly
  // purchases 'unknown', which split them off from every other Spreedly issue in Sentry.
  const logGateway: PaymentGatewayKey = onlinePaymentsGateway ?? 'unknown';
  const runOtpStepUp = useOtpStepUp(logGateway);
  const isStripeCheckout = onlinePaymentsGateway === PaymentGateway.Stripe;

  const resolveWebViewMessage = useRef((message: any): void => {
    if (message?.data?.type !== OnlineShopWebViewMessageTypes.PaymentResult) {
      return;
    }

    handlePaymentResultFromWebView(message.data.payload);
  });

  const handlePaymentResultFromWebView = (payload: OnlineShopWebViewPaymentResponseInterface): void => {
    const { payment_result, order } = payload;

    if (payment_result.status === PaymentStatus.Succeeded) {
      onPaymentSuccess(order);

      return;
    }

    onPaymentError();
  };

  useEffect(() => {
    if (!isMobileApp) {
      return;
    }

    document.addEventListener('message', resolveWebViewMessage.current);

    return () => document.removeEventListener('message', resolveWebViewMessage.current);
  }, [isMobileApp]);

  const onPaymentConfirmationRequired = async (
    response: GiftCardsCheckoutResponse,
    confirmPayment: ConfirmPaymentFunction,
  ): Promise<boolean> => {
    const { status: paymentStatus, error } = await confirmPayment(response.order, response.payment_intent);

    if (paymentStatus === PaymentStatus.Failed) {
      // A refusal by the bank or wallet is the customer's answer too, not ours to alert on.
      // A sheet abandoned with the page is the customer's decision too, and shown to nobody.
      const silent = error?.code === PaymentResultErrorCode.Abandoned;
      const cancelled =
        silent || error?.code === PaymentResultErrorCode.Cancelled || error?.code === PaymentResultErrorCode.Declined;

      if (cancelled) {
        paymentLog.info(logGateway, 'gift_card_payment_cancelled', {
          order_uuid: response.order?.uuid,
          payment_method: selectedPaymentMethod,
        });
      }

      return onPaymentError({ cancelled, silent });
    }

    if (paymentStatus === PaymentStatus.Pending) {
      return false;
    }

    return onPaymentSuccess(response.order);
  };

  const onInputSectionError = (section: SectionType) => {
    toast.error(t('toasts.input_errors_found'));

    toggleCheckoutSection(section, true);
  };

  const onValidationError = async (response: ValidationErrorResponseData): Promise<boolean> => {
    if (
      collect(response.rules)
        .only(['customer_first_name', 'customer_last_name', 'customer_email', 'customer_phone_number'])
        .isNotEmpty()
    ) {
      onInputSectionError(SectionType.customer_info);

      return false;
    }

    if (collect(response.rules).only(['invoice_email']).isNotEmpty()) {
      onInputSectionError(SectionType.invoice_info);

      return false;
    }

    // More gift cards than one purchase may hold: the backend names the maximum.
    if (collect(response.rules).only(['quantity']).isNotEmpty()) {
      toast.error(response.errors?.quantity?.[0] ?? t('toasts.input_errors_found'));

      return false;
    }

    // The CPF for Pix or NuPay, or a method this checkout cannot take: the payment section is where
    // the customer can fix it, with the backend's own words.
    if (collect(response.rules).only(['customer_document', 'payment_method_id']).isNotEmpty()) {
      const message = response.errors?.customer_document?.[0] ?? response.errors?.payment_method_id?.[0];

      toast.error(message ?? t('toasts.input_errors_found'));
      toggleCheckoutSection(SectionType.payment_info, true);

      return false;
    }

    // As in the order checkout: the Stripe Elements checkout itself was turned away (switched off,
    // the method hidden, the payment details expired). The backend's words, and the company read
    // again so the payment section re-renders as the restaurant now has it.
    if (collect(response.rules).only(STRIPE_ELEMENT_RULES).isNotEmpty()) {
      toast.error(stripeElementValidationMessage(response) ?? t('toasts.input_errors_found'));
      refresh(null, true);
      toggleCheckoutSection(SectionType.payment_info, true);

      return false;
    }

    return false;
  };

  const onBadRequestError = async (
    response: AxiosResponse<{ error_code: string }>,
    retry: (headers: IMap<string>, perCallOptions?: CheckoutOptions) => Promise<boolean>,
    options?: CheckoutOptions,
  ): Promise<boolean> => {
    if (response.data.error_code !== ERROR_CODES.OTP_VERIFICATION_REQUIRED) {
      return false;
    }

    options?.onBeforeOtpModalOpen?.();

    return runOtpStepUp(response.headers[OTP_VERIFICATION_PHONE_NUMBER_HEADER], retry);
  };

  const checkout: CheckoutFunction = async (confirmPayment, paymentIntent, extraData, extraHeaders, options) => {
    setLoading(true);

    paymentLog.info(logGateway, 'gift_card_checkout_start', {
      payment_method: selectedPaymentMethod,
      intent_id: paymentIntent?.id,
      is_retry: Boolean(extraHeaders && Object.keys(extraHeaders).length),
    });

    // See useOrderCheckout for the onOtpAccepted contract — fires once the
    // /gift-cards/checkout request returns anything other than a 400
    // OTP_VERIFICATION_REQUIRED so the OTP modal can close on success.
    const signalOtpAccepted = (err?: any) => {
      const isOtpStillFailing =
        err?.status === 400 && err?.data?.response?.data?.error_code === ERROR_CODES.OTP_VERIFICATION_REQUIRED;

      if (!isOtpStillFailing) {
        options?.onOtpAccepted?.();
      }
    };

    return checkoutMtn({
      ...paymentProps,
      ...giftCardBasket,
      // Outside the Stripe Elements checkout the payload is what it always was: the selection wins.
      ...(isStripeCheckout ? {} : extraData),
      companyId,
      payment_intent_id: paymentIntent?.id,
      payment_method_id: selectedPaymentMethod,
      // The card row's consent, as the order checkout sends it: the backend writes it onto the
      // intent it creates at order time. Only the Stripe Elements checkout sends it; the Adyen and
      // Spreedly gift-card paths never did, and their handlers would act on it.
      ...(isStripeCheckout ? { should_save_payment_info: shouldSavePaymentInfo } : {}),
      // Last under the Stripe Elements checkout, so what the Express Checkout or the Payment Element
      // names wins: the wallet's or the Element's own method, and no consent for a wallet's card.
      ...(isStripeCheckout ? extraData : {}),
      headers: extraHeaders,
    })
      .unwrap()
      .then((response: GenericOrder) => {
        signalOtpAccepted();

        paymentLog.info(logGateway, 'gift_card_checkout_succeeded', {
          order_uuid: response?.uuid,
          payment_method: selectedPaymentMethod,
        });

        return onPaymentSuccess(response);
      })
      .catch((err) => {
        signalOtpAccepted(err);

        if (err?.status === ReservedHttpResponseCodes.PaymentConfirmationRequired) {
          paymentLog.info(logGateway, 'gift_card_payment_confirmation_required', {
            payment_method: selectedPaymentMethod,
            order_uuid: err?.data?.response?.data?.order?.uuid,
          });

          return onPaymentConfirmationRequired(err.data.response.data, confirmPayment);
        }

        if (err?.status === ReservedHttpResponseCodes.ValidationError) {
          paymentLog.warn(logGateway, 'gift_card_checkout_validation_error', {
            payment_method: selectedPaymentMethod,
            rule_keys: Object.keys(err?.data?.response?.data?.rules ?? {}).join(','),
          });

          return onValidationError(err.data.response.data);
        }

        if (err?.status === 400) {
          paymentLog.warn(logGateway, 'gift_card_checkout_bad_request', {
            payment_method: selectedPaymentMethod,
            error_code: err?.data?.response?.data?.error_code,
          });

          return onBadRequestError(
            err.data.response,
            (headers: IMap<string>, perCallOptions?: CheckoutOptions) =>
              checkout(confirmPayment, paymentIntent, extraData, headers, { ...options, ...perCallOptions }),
            options,
          );
        }

        // Too many gift-card purchases from this phone or connection, or refused for fraud: as in the
        // order checkout, nothing to complete here, so the support message rather than "internal error".
        const supportEmail = company?.service_provider?.support_email ?? 'support@foodamigos.io';

        if (err?.status === ReservedHttpResponseCodes.TooManyRequests) {
          const retryAfterSeconds = Number(err?.data?.response?.headers?.['retry-after']) || 0;
          const unblockAt = retryAfterSeconds > 0 ? new Date(Date.now() + retryAfterSeconds * 1000) : null;

          paymentLog.warn(logGateway, 'gift_card_checkout_throttled', {
            payment_method: selectedPaymentMethod,
            retry_after_seconds: retryAfterSeconds,
          });
          alert.error({
            marker: unblockAt ? 'order_blocked' : 'order_blocked_no_datetime',
            title: t('error_messages.order_blocked.title'),
            description: unblockAt
              ? t('error_messages.order_blocked.description', {
                  datetime: new Intl.DateTimeFormat(i18n.language, {
                    dateStyle: 'short',
                    timeStyle: 'short',
                  }).format(unblockAt),
                  supportEmail,
                })
              : t('error_messages.order_blocked.description_no_datetime', { supportEmail }),
          });

          return false;
        }

        if (err?.status === 403 && err?.data?.response?.data?.error_code === ERROR_CODES.BLOCKED_FOR_FRAUD) {
          paymentLog.warn(logGateway, 'gift_card_checkout_blocked_for_fraud', {
            payment_method: selectedPaymentMethod,
          });
          alert.error({
            marker: 'blocked_for_fraud',
            title: t('error_messages.order_blocked.title'),
            description: t('error_messages.order_blocked.description_no_datetime', { supportEmail }),
          });

          return false;
        }

        if (
          [ReservedHttpResponseCodes.ErrorResponseModal, ReservedHttpResponseCodes.ErrorResponseAlertWindow].includes(
            err?.status,
          )
        ) {
          paymentLog.warn(logGateway, 'gift_card_checkout_handled_error', {
            payment_method: selectedPaymentMethod,
            status: err?.status,
          });

          return false;
        }

        // A thrown Error is our own code failing (RTK rejects responses with plain objects), and a
        // 4xx nothing above handles is a response this client was never taught to read. A 5xx is
        // already the backend's Sentry issue, and FETCH_ERROR is the network.
        const isCodeFailure =
          err instanceof Error || (typeof err?.status === 'number' && err.status >= 400 && err.status < 500);

        paymentLog[isCodeFailure ? 'issue' : 'error'](logGateway, 'gift_card_checkout_internal_error', {
          payment_method: selectedPaymentMethod,
          status: err?.status,
          message: err?.message,
        });

        alert.error({
          title: t('error_messages.internal_error.title'),
          description: t('error_messages.internal_error.description'),
        });

        return false;
      })
      .finally(() => setLoading(false));
  };

  return {
    data,
    checkout,
    loading: _loading,
  };
};

export default useGiftCardCheckout;
